import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronDown } from "lucide-react";
import { fetchEnabledCountries } from "../../../util/countryManagement";
import { setSettingsSlice } from "../../../features/userSettingsSlice";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchCurrencyRates } from "../../../features/currencySlice";
import {
  getCurrencySymbol,
  normalizeCurrencyRates,
} from "../../../util/money";
import { writeScopedJson } from "../../../util/privacyConsent";
import {
  getAllowedCurrenciesForCountry,
  resolveCurrencyForCountry as resolvePolicyCurrencyForCountry,
} from "../../../util/currencyPolicy";

/* =========================
   CONSTANTS
========================= */
const SETTINGS_STORAGE_KEY = "user_settings";

// Import flag CDN configuration
// import { getFlagCdnUrl, setFlagsCdnDomain } from "@/config/flagsConfig";
// OR use directly in this file if not using the separate config:

// CDN Configuration for flag images
const FLAGS_CDN_CONFIG = {
  // Change the domain here to point to your CDN
  domain: "https://raw.githubusercontent.com/hawkeye-bd/cherrfy-cdn/23adf5297fe759475876c97f6feec577ab923fef/flags/",
  // Alternative CDN options:
  // domain: "https://cdn.yoursite.com/assets/flags",
  // domain: "https://flagcdn.com",
  // domain: "/assets/images/flags", // Local path
};

// Function to get the full CDN URL for a flag
const getFlagCdnUrl = (countryCode) => {
  const cc = String(countryCode || "").trim().toLowerCase();
  if (!cc) return null;
  return `${FLAGS_CDN_CONFIG.domain}/${cc}.svg`;
};

const defaultSettings = {
  shipToCountry: "US",
  currency: "USD",
  language: "en",
};

const CURRENCY_NAME_OVERRIDES = {
  USD: "Dollar",
  BDT: "Bangladeshi Taka",
  BRL: "Brazilian Real",
  EUR: "Euro",
  GBP: "British Pound",
  CAD: "Canadian Dollar",
  AUD: "Australian Dollar",
  AED: "UAE Dirham",
  ILS: "Israeli Shekel",
  TRY: "Turkish Lira",
  MXN: "Mexican Peso",
  ARS: "Argentine Peso",
  ZAR: "South African Rand",
  CHF: "Swiss Franc",
  SEK: "Swedish Krona",
  NOK: "Norwegian Krone",
  DKK: "Danish Krone",
  PLN: "Polish Zloty",
  CZK: "Czech Koruna",
  HUF: "Hungarian Forint",
  RON: "Romanian Leu",
  BGN: "Bulgarian Lev",
  NZD: "New Zealand Dollar",
  JPY: "Japanese Yen",
  INR: "Indian Rupee",
};

function getCurrencyLabel(code) {
  const cur = String(code || "").trim().toUpperCase();
  if (!cur) return "";

  const override = CURRENCY_NAME_OVERRIDES[cur];
  if (override) return `${cur} (${override})`;

  try {
    // Modern browsers: nice localized currency names.
    if (typeof Intl !== "undefined" && typeof Intl.DisplayNames !== "undefined") {
      const dn = new Intl.DisplayNames(["en"], { type: "currency" });
      const name = dn.of(cur);
      if (name) return `${cur} (${name})`;
    }
  } catch {
    // ignore
  }

  return cur;
}

const normalizeCurrencyCode = (value) => {
  const cur = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(cur) ? cur : "";
};

const resolveCurrencyForCountry = (countryCode, enabledCountries = [], currencyPayload = null) => {
  const cc = String(countryCode || "").trim().toUpperCase();
  if (!cc) return "USD";

  const country = (enabledCountries || []).find(
    (c) => String(c?.countryCode || "").trim().toUpperCase() === cc
  );
  if (country) return resolvePolicyCurrencyForCountry(country, null, { allowCandidate: false });

  const rateCurrency = normalizeCurrencyCode(
    currencyPayload?.countryCurrencies?.[cc] ||
    currencyPayload?.rates?.[cc]?.currency ||
    currencyPayload?.rates?.[cc]?.currencyCode ||
    currencyPayload?.rates?.[cc]?.code
  );
  if (rateCurrency) return rateCurrency;

  return "USD";
};

// Flag image component
const FlagImage = ({ countryCode, alt, className = "", style = {} }) => {
  const url = getFlagCdnUrl(countryCode);
  if (!url) return null;
  return (
    <img
      src={url}
      alt={alt || `${countryCode} flag`}
      className={className}
      style={style}
      loading="lazy"
      onError={(e) => {
        // Fallback: add a border to indicate missing image
        e.target.style.border = "1px solid #e2e8f0";
      }}
    />
  );
};


const SettingsSidebar = ({ hideTriggerOnMobile = false }) => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { shipToCountry, currency, language } = useSelector((state) => state.userSettings);
  const currencyPayload = useSelector((state) => state?.currency?.currency_list);
  const currencyStatus = useSelector((state) => state?.currency?.status);
  const countrySource = useSelector((state) => state?.userSettings?.countrySource);
  const detectedCountry = useSelector((state) => state?.userSettings?.detectedCountry);
  const userOverride = useSelector((state) => state?.userSettings?.userOverride);

  const [isOpen, setIsOpen] = useState(false);
  const [openFromBottom, setOpenFromBottom] = useState(false);
  const ignoreOutsideRef = useRef(false);
  const rootRef = useRef(null);

  const [availableCurrenciesLoading, setAvailableCurrenciesLoading] = useState(false);
  const [availableCurrenciesError, setAvailableCurrenciesError] = useState(null);
  const [enabledCountries, setEnabledCountries] = useState([]);
  const isFullscreen = openFromBottom;
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const shouldFullscreen = isFullscreen || isMobileViewport;

  // Local editable state (drives UI)
  const [settings, setSettings] = useState(() => {
    return {
      ...defaultSettings,
      shipToCountry: shipToCountry || defaultSettings.shipToCountry,
      currency: currency || defaultSettings.currency,
      language: language || defaultSettings.language,
    };
  });

  const [openList, setOpenList] = useState(null);

  useEffect(() => {
    if (!isOpen) setOpenList(null);
  }, [isOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobileViewport(Boolean(mq.matches));
    update();
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

    /* =========================
      LOAD ENABLED COUNTRIES
      (source of allowed countries + currencies)
    ========================= */
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setAvailableCurrenciesError(null);
      setAvailableCurrenciesLoading(true);
      try {
        const countries = await fetchEnabledCountries();
        if (!mounted) return;
        setEnabledCountries(Array.isArray(countries) ? countries : []);
      } catch (e) {
        if (!mounted) return;
        setAvailableCurrenciesError(e?.response?.data?.message || e?.message || "Failed to load countries");
        setEnabledCountries([]);
      } finally {
        if (mounted) setAvailableCurrenciesLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  // Ensure we have currency rates in redux (App usually loads it, but this is a safe fallback).
  useEffect(() => {
    if (currencyPayload) return;
    if (currencyStatus === "loading") return;
    dispatch(fetchCurrencyRates());
  }, [currencyPayload, currencyStatus, dispatch]);

  const countryLabelByCode = useMemo(() => {
    const map = {};
    (enabledCountries || []).forEach((country) => {
      const code = String(country?.countryCode || "").trim().toUpperCase();
      if (!code) return;
      map[code] = country?.countryName || code;
    });
    return map;
  }, [enabledCountries]);

  // Non-blocking geo provenance notice (audit 4-FE). Hidden once the visitor
  // has explicitly chosen a store, and for confirmed sources (mmdb/cache/lookup).
  const geoNotice = useMemo(() => {
    if (userOverride) return null;
    const resolvedLabel =
      countryLabelByCode?.[String(shipToCountry || "").trim().toUpperCase()] ||
      shipToCountry ||
      "US";
    if (countrySource === "fallback-disabled") {
      const detectedLabel =
        countryLabelByCode?.[String(detectedCountry || "").trim().toUpperCase()] ||
        detectedCountry;
      return detectedLabel
        ? `${detectedLabel} store is unavailable — showing ${resolvedLabel}.`
        : `That store is unavailable — showing ${resolvedLabel}.`;
    }
    if (
      countrySource === "default" ||
      countrySource === "no-enabled" ||
      countrySource === "lookup-failed"
    ) {
      return `We couldn’t confirm your location — shopping the ${resolvedLabel} store.`;
    }
    return null;
  }, [countryLabelByCode, countrySource, detectedCountry, shipToCountry, userOverride]);

  const currencyOptions = useMemo(() => {
    const rates = normalizeCurrencyRates(currencyPayload);
    const selectedCountryCode = String(settings.shipToCountry || "").trim().toUpperCase();
    const selectedCountry = (enabledCountries || []).find(
      (country) => String(country?.countryCode || "").trim().toUpperCase() === selectedCountryCode
    );
    const codes = getAllowedCurrenciesForCountry(selectedCountry);

    return codes
      .filter((code) => code === "USD" || Number(rates[code]) > 0)
      .sort((a, b) => {
        if (a === "USD") return -1;
        if (b === "USD") return 1;
        return a.localeCompare(b);
      })
      .map((value) => ({ value, label: getCurrencyLabel(value) || value }));
  }, [currencyPayload, enabledCountries, settings.shipToCountry]);

  // Browsing currency remains independent from the shipping destination.
  useEffect(() => {
    const allowed = new Set(currencyOptions.map((o) => o.value));
    if (!allowed.size) return;
    if (allowed.has(settings.currency)) return;

    setSettings((p) => ({
      ...p,
      currency: resolveCurrencyForCountry(
        p.shipToCountry,
        enabledCountries,
        currencyPayload
      ),
    }));
  }, [currencyOptions, currencyPayload, enabledCountries, settings.currency]);

  useEffect(() => {
    if (!isOpen) return;
    setSettings({
      ...defaultSettings,
      shipToCountry: shipToCountry || defaultSettings.shipToCountry,
      currency: currency || defaultSettings.currency,
      language: language || defaultSettings.language,
    });
    setOpenList(null);
  }, [isOpen, shipToCountry, currency, language]);

  /* =========================
     ESC + CLICK OUTSIDE CLOSE
     (MyCartWidget-style)
  ========================= */
  useEffect(() => {
    if (!isOpen) return;

    const onDown = (e) => {
      if (shouldFullscreen) return;
      if (ignoreOutsideRef.current) return;
      const el = rootRef.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      setIsOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, shouldFullscreen]);

  useEffect(() => {
    if (!isOpen || !shouldFullscreen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, shouldFullscreen]);

  useEffect(() => {
    const handleOpen = () => {
      ignoreOutsideRef.current = true;
      setOpenFromBottom(true);
      setIsOpen(true);
      setTimeout(() => {
        ignoreOutsideRef.current = false;
      }, 0);
    };
    const handleClose = () => setIsOpen(false);
    window.addEventListener("cherrfy:open-settings", handleOpen);
    window.addEventListener("cherrfy:close-settings", handleClose);
    return () => {
      window.removeEventListener("cherrfy:open-settings", handleOpen);
      window.removeEventListener("cherrfy:close-settings", handleClose);
    };
  }, []);

  /* =========================
     UI
  ========================= */
  const summaryLine = useMemo(() => {
    const label = countryLabelByCode?.[String(shipToCountry || "").trim().toUpperCase()];
    return `${label || shipToCountry} • ${currency}`;
  }, [countryLabelByCode, shipToCountry, currency]);

  const languageOptions = useMemo(
    () => [
      { value: "en", label: "English" },
      { value: "bn", label: "বাংলা" },
    ],
    []
  );

  const languageCode = String(language || settings.language || "en").trim().slice(0, 2).toUpperCase();
  const settingsCurrency = String(currency || settings.currency || "USD").trim().toUpperCase();

  const handleSave = () => {
    const nextCountry = String(settings.shipToCountry || defaultSettings.shipToCountry).trim().toUpperCase();
    const countryCurrency = resolveCurrencyForCountry(nextCountry, enabledCountries, currencyPayload);
    const selectedCurrency = normalizeCurrencyCode(settings.currency);
    const allowedCurrencies = new Set(currencyOptions.map((option) => option.value));
    const nextCurrency = allowedCurrencies.has(selectedCurrency) ? selectedCurrency : countryCurrency;
    const nextSettings = {
      ...settings,
      shipToCountry: nextCountry,
      currency: nextCurrency,
      language: settings.language || defaultSettings.language,
      userOverride: true,
    };

    writeScopedJson(SETTINGS_STORAGE_KEY, nextSettings, { persistWhenAccepted: true });
    dispatch(setSettingsSlice(nextSettings));
    i18n.changeLanguage(nextSettings.language);
    setIsOpen(false);
  };

  const countryOptions = useMemo(() => {
    const list = (enabledCountries || [])
      .map((c) => ({
        value: String(c?.countryCode || "").trim().toUpperCase(),
        label: c?.countryName || c?.countryCode,
      }))
      .filter((o) => o.value && o.label);
    list.sort((a, b) => String(a.label).localeCompare(String(b.label)));

    if (!list.length && settings.shipToCountry) {
      list.push({
        value: String(settings.shipToCountry || "").trim().toUpperCase(),
        label: countryLabelByCode?.[String(settings.shipToCountry || "").trim().toUpperCase()] ||
          String(settings.shipToCountry || "").trim().toUpperCase(),
      });
    }

    return list;
  }, [enabledCountries, countryLabelByCode, settings.shipToCountry]);

  useEffect(() => {
    if (!isOpen) return;
    if (!countryOptions.length) return;
    const current = String(settings.shipToCountry || "").trim().toUpperCase();
    const allowed = new Set(countryOptions.map((o) => o.value));
    if (allowed.has(current)) return;

    const nextCountry = countryOptions[0].value;
    const nextCurrency = resolveCurrencyForCountry(nextCountry, enabledCountries, currencyPayload);
    setSettings((p) => ({
      ...p,
      shipToCountry: nextCountry,
      currency: nextCurrency,
    }));
  }, [countryOptions, currencyPayload, enabledCountries, isOpen, settings.shipToCountry]);

  const ListRowSelect = ({
    label,
    name,
    value,
    onChange,
    options,
    getOptionLabel,
    renderSelected,
    renderOption,
  }) => {
    const isExpanded = openList === name;
    const listboxRef = useRef(null);
    const selectedLabel = useMemo(() => {
      const found = (options || []).find((o) => o.value === value);
      if (!found) return getOptionLabel ? getOptionLabel({ value }) : String(value ?? "");
      return getOptionLabel ? getOptionLabel(found) : found.label;
    }, [options, value, getOptionLabel]);

    useEffect(() => {
      if (!isExpanded) return;
      const root = listboxRef.current;
      if (!root) return;

      const selectedEl = root.querySelector(`[data-value="${String(value)}"]`);
      if (!selectedEl) return;

      const scrollToSelectedCenter = () => {
        const rootRect = root.getBoundingClientRect();
        const elRect = selectedEl.getBoundingClientRect();
        const currentScroll = root.scrollTop;
        const elTop = elRect.top - rootRect.top + currentScroll;

        const target = elTop - root.clientHeight / 2 + elRect.height / 2;
        const maxScroll = Math.max(0, root.scrollHeight - root.clientHeight);
        root.scrollTop = Math.max(0, Math.min(maxScroll, target));
      };

      requestAnimationFrame(() => requestAnimationFrame(scrollToSelectedCenter));
    }, [isExpanded, value]);

    return (
      <div className="bg-white">
        <p className="mb-1.5 text-sm font-medium tracking-tight text-slate-900 sm:text-base">
          {label}
        </p>
        <button
          type="button"
          onClick={() => setOpenList((cur) => (cur === name ? null : name))}
          className="flex h-10 w-full items-center gap-2 rounded-xl border-[0.5px] border-slate-200/70 bg-white px-3 text-left transition-colors hover:border-slate-200"
          aria-expanded={isExpanded}
          aria-label={label}
        >
          <span className="min-w-0 flex-1">
            {renderSelected ? (
              renderSelected({ value, label: selectedLabel })
            ) : (
              <span className="block truncate text-xs font-semibold text-slate-950 sm:text-sm">{selectedLabel}</span>
            )}
          </span>
          <span
            className={"text-slate-950 transition-transform " + (isExpanded ? "rotate-180" : "")}
            aria-hidden="true"
          >
            <ChevronDown size={16} strokeWidth={2} />
          </span>
        </button>

        {isExpanded ? (
          <div className="pt-2">
            <div role="listbox" aria-label={label + " options"} className="overflow-hidden rounded-xl border-[0.5px] border-slate-200/70 bg-white">
              <div ref={listboxRef} className="max-h-44 overflow-y-auto divide-y divide-slate-100">
                {(options || []).map((opt) => {
                  const isSelected = opt.value === value;
                  const optLabel = getOptionLabel ? getOptionLabel(opt) : opt.label;
                  return (
                    <button
                      key={String(opt.value)}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-value={String(opt.value)}
                      onClick={() => {
                        onChange(opt.value);
                        setOpenList(null);
                      }}
                      className={[
                        "flex w-full items-center justify-between gap-2 px-3 py-2 text-left",
                        "hover:bg-slate-50",
                        isSelected ? "bg-[#D2042D]/8" : "bg-white",
                      ].join(" ")}
                    >
                      <span className="min-w-0 flex-1">
                        {renderOption ? (
                          renderOption({ opt, isSelected, label: optLabel })
                        ) : (
                          <span
                            className={
                              "block truncate text-xs font-semibold " +
                              (isSelected ? "text-[#D2042D]" : "text-slate-800")
                            }
                          >
                            {optLabel}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const dialog = (
    <div
      role="dialog"
      aria-modal={isOpen ? true : undefined}
      aria-hidden={!isOpen}
      aria-label="Shipping and currency settings"
      inert={isOpen ? undefined : ""}
      className={[
        shouldFullscreen
          ? "fixed inset-0 z-[110] bg-white"
          : "absolute right-0 top-full z-[120] mt-2 w-[min(340px,calc(100vw-2rem))]",
        shouldFullscreen ? "max-w-none" : "max-w-[calc(100vw-1rem)] md:max-w-none",
        shouldFullscreen
          ? "rounded-none"
          : "rounded-3xl border-[0.5px] border-slate-200/70 bg-white",
        shouldFullscreen
          ? "transform-gpu transition-all duration-200 ease-out"
          : "origin-top-right transform-gpu transition-all duration-200 ease-out",
        isOpen
          ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
          : shouldFullscreen
            ? "opacity-0 pointer-events-none"
            : "opacity-0 -translate-y-1 scale-[0.98] pointer-events-none",
      ].join(" ")}
    >
      {!shouldFullscreen && (
        <div className="absolute -top-2 right-16 h-5 w-5 rotate-45 border-l border-t border-slate-200/70 bg-white" />
      )}
      <div className={shouldFullscreen ? "flex h-full flex-col p-4" : "rounded-3xl bg-white p-4"}>
        <div className="mb-1 flex justify-end">
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-950"
            aria-label="Close settings"
          >
            <X size={17} />
          </button>
        </div>

        <div className={shouldFullscreen ? "flex-1 overflow-y-auto pb-6" : ""}>
          <div className="space-y-3.5">
            <ListRowSelect
              label="Ship to"
              name="shipToCountry"
              value={settings.shipToCountry}
              onChange={(next) => {
                const code = String(next || "").trim().toUpperCase();
                setSettings((p) => ({
                  ...p,
                  shipToCountry: code,
                  currency: resolveCurrencyForCountry(code, enabledCountries, currencyPayload),
                }));
              }}
              options={countryOptions}
              renderSelected={({ value: vv, label: ll }) => (
                <span className="flex min-w-0 items-center gap-2">
                  <FlagImage
                    countryCode={vv}
                    alt={ll}
                    className="h-3.5 w-6 flex-shrink-0 rounded-sm object-cover"
                  />
                  <span className="block truncate text-xs font-semibold text-slate-950 sm:text-sm">{ll}</span>
                </span>
              )}
              renderOption={({ opt, isSelected, label: ll }) => (
                <span className="flex min-w-0 items-center gap-2">
                  <FlagImage
                    countryCode={opt?.value}
                    alt={ll}
                    className="h-3.5 w-6 flex-shrink-0 rounded-sm object-cover"
                  />
                  <span
                    className={
                      "block truncate text-xs font-semibold " +
                      (isSelected ? "text-[#D2042D]" : "text-slate-800")
                    }
                  >
                    {ll}
                  </span>
                </span>
              )}
            />

            {geoNotice ? (
              <p className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-500">
                {geoNotice}
              </p>
            ) : null}

            <ListRowSelect
              label="Language"
              name="language"
              value={settings.language}
              onChange={(next) => {
                setSettings((p) => ({ ...p, language: next }));
              }}
              options={languageOptions}
            />

            <ListRowSelect
              label="Currency"
              name="currency"
              value={settings.currency}
              onChange={(next) => {
                setSettings((p) => ({ ...p, currency: next }));
              }}
              options={
                availableCurrenciesLoading
                  ? [{ value: settings.currency, label: settings.currency || "Loading…" }]
                  : currencyOptions.length
                    ? currencyOptions
                    : [{ value: settings.currency || "", label: settings.currency || "" }]
              }
              getOptionLabel={(o) => o.label || String(o.value)}
              renderSelected={({ value: vv, label: ll }) => {
                const symbol = getCurrencySymbol(vv);
                return (
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className="w-5 flex-shrink-0 text-center text-sm font-medium text-slate-500"
                      aria-hidden="true"
                    >
                      {symbol || "¤"}
                    </span>
                    <span className="block truncate text-xs font-semibold text-slate-950 sm:text-sm">{ll}</span>
                  </span>
                );
              }}
              renderOption={({ opt, isSelected, label: ll }) => {
                const symbol = getCurrencySymbol(opt?.value);
                return (
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className={
                        "w-5 flex-shrink-0 text-center text-xs font-medium " +
                        (isSelected ? "text-[#D2042D]" : "text-slate-500")
                      }
                      aria-hidden="true"
                    >
                      {symbol || "¤"}
                    </span>
                    <span
                      className={
                        "block text-xs font-semibold truncate " +
                        (isSelected ? "text-[#D2042D]" : "text-slate-800")
                      }
                    >
                      {ll}
                    </span>
                  </span>
                );
              }}
            />

            {availableCurrenciesError ? (
              <div>
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                  {availableCurrenciesError}
                </div>
              </div>
            ) : null}
          </div>

          <button
            type="button"
            onClick={handleSave}
            className="mt-4 h-10 w-full rounded-md bg-slate-950 text-sm font-medium text-white transition-colors hover:bg-black"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div ref={rootRef} className={shouldFullscreen ? "z-[110]" : "relative z-50"}>
      <button
        type="button"
        onClick={() => {
          setOpenFromBottom(false);
          setIsOpen((v) => !v);
        }}
        className={[
          hideTriggerOnMobile ? "hidden md:inline-flex" : "inline-flex",
          "h-10 items-center gap-1.5 px-1.5 text-slate-900 transition-colors hover:text-[#D2042D] lg:h-11 lg:gap-2 lg:px-2",
          "focus:outline-none focus:ring-1 focus:ring-slate-300",
        ].join(" ")}
        aria-label={`Open shipping and currency settings: ${summaryLine}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <div className="inline-flex h-4 w-7 shrink-0 items-center justify-center overflow-hidden bg-white lg:h-5 lg:w-8">
          <FlagImage
            countryCode={shipToCountry || settings.shipToCountry}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="hidden text-left leading-tight md:block">
          <p className="text-[11px] font-normal text-slate-900">{languageCode}/</p>
          <p className="-mt-0.5 text-[11px] font-normal text-slate-900">{settingsCurrency}</p>
        </div>
        <ChevronDown size={16} strokeWidth={2.3} aria-hidden="true" />
      </button>

      {shouldFullscreen
        ? (typeof document !== "undefined" ? createPortal(dialog, document.body) : null)
        : dialog}
    </div>
  );
};

export default SettingsSidebar;
